import { getVideoAspectRatio } from "@ui/molecules/VideoCarousel/VideoCarousel";
import type { JSX } from "solid-js";
import { render } from "solid-js/web";
import { InteractiveTableView } from "../components/InteractiveTableView.tsx";
import { MentionedVideos } from "../components/MentionedVideos.tsx";
import { MentionedVideosList } from "../components/MentionedVideosList.tsx";
import { MentionedVideoThumbnail } from "../components/MentionedVideoThumbnail.tsx";
import type { MentionedVideoItem } from "../components/VideoMention.tsx";
import { parseTableElement } from "./parseTableElement";
import type { PlayVideoMentionsHandler } from "./videoMentionPlayback";

type MentionedVideoMountOptions = {
	allVideoMentions: MentionedVideoItem[];
	appEmbedded: boolean;
	offersClipboard?: boolean;
	onPlayVideoMentions?: PlayVideoMentionsHandler;
	disposers: Array<() => void>;
};

export function hydrateMentionPlaceholders(
	container: HTMLElement,
	placeholders: Map<string, () => JSX.Element>,
	disposers: Array<() => void>,
) {
	for (const [id, renderContent] of placeholders.entries()) {
		const placeholder = container.querySelector(
			`[data-mention-placeholder="${CSS.escape(id)}"]`,
		);
		if (!placeholder) {
			continue;
		}

		const mountPoint = document.createElement("span");
		mountPoint.style.display = "inline";
		const dispose = render(renderContent, mountPoint);
		disposers.push(dispose);
		placeholder.replaceWith(mountPoint);
	}
}

const HEADING_TAGS = new Set(["H1", "H2", "H3", "H4", "H5", "H6"]);

/**
 * True once a markdown table has its `|---|` separator. Before that a single
 * pipe line renders as a table too, but one that can still turn back into text.
 */
export function tableHasHeaderRow(table: HTMLTableElement): boolean {
	return table.rows[0]?.cells[0]?.tagName === "TH";
}

/**
 * The heading directly before `el`, marked as the title an interactive table
 * takes into its toolbar. Mentioned-video inserts are skipped: they are placed
 * again after every render, so may still sit where the last pass left them.
 */
export function headingBefore(el: Node): HTMLElement | undefined {
	for (let node = el.previousSibling; node; node = node.previousSibling) {
		if (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()) {
			continue;
		}
		if (
			node instanceof HTMLElement &&
			(node.hasAttribute("data-mentioned-videos-insert") ||
				node.hasAttribute("data-mentioned-video-thumbnail-insert"))
		) {
			continue;
		}
		if (node instanceof HTMLElement && HEADING_TAGS.has(node.tagName)) {
			node.classList.add("interactive-table-heading");
			return node;
		}
		return undefined;
	}
	return undefined;
}

export function hydrateInteractiveTables(
	container: HTMLElement,
	options: {
		idBase: string;
		theme?: "standard" | "borderless";
		disposers: Array<() => void>;
	},
) {
	const theme = options.theme ?? "borderless";
	let tableIndex = 0;

	for (const table of container.querySelectorAll("table")) {
		const id = `${options.idBase}-tbl-${tableIndex++}`;
		const { headers, rows } = parseTableElement(table as HTMLTableElement);
		const heading = tableHasHeaderRow(table) ? headingBefore(table) : undefined;
		const mountPoint = document.createElement("div");
		mountPoint.style.display = "contents";
		const dispose = render(
			() => (
				<InteractiveTableView
					id={id}
					theme={theme}
					heading={heading}
					headers={headers}
					rows={rows}
				/>
			),
			mountPoint,
		);
		options.disposers.push(dispose);
		table.replaceWith(mountPoint);
	}
}

const MAJOR_BLOCK_SELECTOR = "h1,h2,h3,h4,h5,h6,p,ol,ul";

export type MentionedVideosPlacement = "afterBlock" | "atEnd";

/** Where to mount aggregated mentioned-video lists after rich-text hydration. */
export const MENTIONED_VIDEOS_PLACEMENT: MentionedVideosPlacement =
	"afterBlock";

const MAJOR_BLOCK_QUERY = `${MAJOR_BLOCK_SELECTOR},.interactive-table-container`;

function isVideoMentionInTable(el: Element): boolean {
	return el.closest("table,.interactive-table-container") !== null;
}

function isPortraitMention(item: MentionedVideoItem): boolean {
	return getVideoAspectRatio(item) <= 1;
}

function collectMajorBlocksInOrder(container: HTMLElement): HTMLElement[] {
	const candidates = Array.from(
		container.querySelectorAll(MAJOR_BLOCK_QUERY),
	) as HTMLElement[];

	return candidates.filter(
		(block) =>
			!candidates.some((other) => other !== block && other.contains(block)),
	);
}

/** True when a markdown major block is followed by another speaking-card segment. */
function hasFollowingSpeakingSegmentContent(
	block: HTMLElement,
	container: HTMLElement,
): boolean {
	if (!container.classList.contains("speaking-card-content")) {
		return false;
	}

	const segment = block.closest(".speaking-markdown-segment");
	if (!(segment instanceof HTMLElement) || segment.parentElement !== container) {
		return false;
	}

	for (
		let sibling = segment.nextElementSibling;
		sibling;
		sibling = sibling.nextElementSibling
	) {
		if (!(sibling instanceof HTMLElement)) {
			continue;
		}
		if (
			sibling.classList.contains("speaking-markdown-segment") ||
			sibling.classList.contains("artifact-mention")
		) {
			return true;
		}
	}

	return false;
}

function shouldFlushMajorBlockPlacements(
	block: HTMLElement,
	blockIndex: number,
	majorBlocks: HTMLElement[],
	container: HTMLElement,
	answerComplete?: boolean,
): boolean {
	if (answerComplete === true) {
		return true;
	}
	if (blockIndex < majorBlocks.length - 1) {
		return true;
	}
	return hasFollowingSpeakingSegmentContent(block, container);
}

type BlockVideoPlacements = {
	/** Single portrait mention: floated thumbnail beside the block. */
	portraitThumbnail: MentionedVideoItem | null;
	/** Single landscape mention: centered thumbnail in its own block, no carousel. */
	standaloneThumbnail: MentionedVideoItem | null;
	/** Multiple mentions (or in-table mentions): scrollable carousel. */
	carouselMentions: MentionedVideoItem[];
};

function classifyBlockPlacements(
	allMentionsInBlock: MentionedVideoItem[],
	outsideTableMentions: MentionedVideoItem[],
): BlockVideoPlacements {
	if (allMentionsInBlock.length === 1 && outsideTableMentions.length === 1) {
		const onlyMention = outsideTableMentions[0];
		if (isPortraitMention(onlyMention)) {
			return {
				portraitThumbnail: onlyMention,
				standaloneThumbnail: null,
				carouselMentions: [],
			};
		}
		// A lone landscape mention doesn't need carousel chrome; show it as a
		// single centered thumbnail instead.
		return {
			portraitThumbnail: null,
			standaloneThumbnail: onlyMention,
			carouselMentions: [],
		};
	}

	return {
		portraitThumbnail: null,
		standaloneThumbnail: null,
		carouselMentions: allMentionsInBlock,
	};
}

function groupVideoMentionsByBlock(
	container: HTMLElement,
	resolveVideoMention: (id: string) => MentionedVideoItem | undefined,
): Map<HTMLElement, BlockVideoPlacements> {
	const mentionsByBlock = new Map<
		HTMLElement,
		{
			all: MentionedVideoItem[];
			outsideTable: MentionedVideoItem[];
		}
	>();

	for (const el of container.querySelectorAll(".video-mention")) {
		const id = el.getAttribute("data-video-mention-id");
		if (!id) {
			continue;
		}

		const videoMention = resolveVideoMention(id);
		if (!videoMention) {
			continue;
		}

		const block = resolveMentionBlockAnchor(el, container);
		const entry = mentionsByBlock.get(block) ?? {
			all: [],
			outsideTable: [],
		};

		if (!entry.all.some((mention) => mention.id === videoMention.id)) {
			entry.all.push(videoMention);
		}

		if (
			!isVideoMentionInTable(el) &&
			!entry.outsideTable.some((mention) => mention.id === videoMention.id)
		) {
			entry.outsideTable.push(videoMention);
		}

		mentionsByBlock.set(block, entry);
	}

	const placements = new Map<HTMLElement, BlockVideoPlacements>();
	for (const [block, entry] of mentionsByBlock.entries()) {
		placements.set(
			block,
			classifyBlockPlacements(entry.all, entry.outsideTable),
		);
	}

	return placements;
}

function isListItemBlock(block: HTMLElement): boolean {
	return block.tagName === "LI";
}

/**
 * True when `block` is a paragraph whose only rendered content is video-mention
 * citation marker(s): once the markers are stripped, no text or other meaningful
 * element remains. Such a paragraph exists purely to host a citation, so when the
 * same citation is also surfaced by an adjacent mentioned-video block it can be
 * collapsed (see `applyBlockVideoPlacements`).
 */
function isMentionOnlyBlock(block: HTMLElement): boolean {
	if (block.tagName !== "P") {
		return false;
	}
	if (block.querySelector(".video-mention") === null) {
		return false;
	}
	// Clone so we can strip markers without touching the live DOM (the marker
	// spans must stay in place for subsequent re-render/enrichment passes).
	const withoutMarkers = block.cloneNode(true) as HTMLElement;
	for (const marker of withoutMarkers.querySelectorAll(".video-mention")) {
		marker.remove();
	}
	if ((withoutMarkers.textContent ?? "").trim() !== "") {
		return false;
	}
	// Guard against paragraphs that embed non-text content beside the marker.
	return (
		withoutMarkers.querySelector("img,svg,a,table,ul,ol,button,video") === null
	);
}

function shouldFlushListItemBlock(
	listItem: HTMLElement,
	answerComplete?: boolean,
): boolean {
	if (answerComplete === true) {
		return true;
	}

	const next = listItem.nextElementSibling;
	return next instanceof HTMLElement && next.tagName === "LI";
}

function resolveMentionBlockAnchor(
	el: Element,
	container: HTMLElement,
): HTMLElement {
	const interactiveTable = el.closest(".interactive-table-container");
	if (interactiveTable instanceof HTMLElement) {
		return interactiveTable;
	}

	const table = el.closest("table");
	if (table instanceof HTMLElement) {
		return table;
	}

	const listItem = el.closest("li");
	if (listItem instanceof HTMLElement && !isVideoMentionInTable(el)) {
		return listItem;
	}

	const block = el.closest(MAJOR_BLOCK_SELECTOR);
	return (block as HTMLElement | null) ?? container;
}

/**
 * Walks hydrated `.video-mention` spans in document order and returns the
 * matching mention metadata, skipping duplicate ids when the same video is
 * cited more than once in the text.
 */
function collectVideoMentionsInOrder(
	container: HTMLElement,
	resolveVideoMention: (id: string) => MentionedVideoItem | undefined,
): MentionedVideoItem[] {
	const videoMentions: MentionedVideoItem[] = [];
	const seenIds = new Set<string>();

	for (const el of container.querySelectorAll(".video-mention")) {
		const id = el.getAttribute("data-video-mention-id");
		if (!id || seenIds.has(id)) {
			continue;
		}

		const videoMention = resolveVideoMention(id);
		if (!videoMention) {
			continue;
		}

		seenIds.add(id);
		videoMentions.push(videoMention);
	}

	return videoMentions;
}

/**
 * Renders a floated `MentionedVideoThumbnail` before a major block.
 */
function mountMentionedVideoThumbnail(
	videoMention: MentionedVideoItem,
	options: MentionedVideoMountOptions & {
		floatSide: "left" | "right";
	},
): HTMLDivElement {
	const mountPoint = document.createElement("div");
	mountPoint.setAttribute("data-mentioned-video-thumbnail-insert", "true");
	const dispose = render(
		() => (
			<MentionedVideoThumbnail
				videoMention={videoMention}
				allVideoMentions={options.allVideoMentions}
				appEmbedded={options.appEmbedded}
				offersClipboard={options.offersClipboard}
				onPlayVideoMentions={options.onPlayVideoMentions}
				floatSide={options.floatSide}
			/>
		),
		mountPoint,
	);
	options.disposers.push(dispose);
	return mountPoint;
}

/**
 * Renders a single centered `MentionedVideoThumbnail` (carousel styling, no
 * carousel chrome) as a block-level insert. Uses the same
 * `data-mentioned-videos-insert` marker as the carousel so it is cleared and
 * re-created on every insert run.
 */
function mountMentionedVideoBlock(
	videoMention: MentionedVideoItem,
	options: MentionedVideoMountOptions,
): HTMLDivElement {
	const mountPoint = document.createElement("div");
	mountPoint.setAttribute("data-mentioned-videos-insert", "true");
	const dispose = render(
		() => (
			<div class="mentioned-video-single">
				<MentionedVideoThumbnail
					videoMention={videoMention}
					allVideoMentions={options.allVideoMentions}
					appEmbedded={options.appEmbedded}
					offersClipboard={options.offersClipboard}
					onPlayVideoMentions={options.onPlayVideoMentions}
					variant="carousel"
				/>
			</div>
		),
		mountPoint,
	);
	options.disposers.push(dispose);
	return mountPoint;
}

/**
 * Renders a `MentionedVideos` carousel after a major block.
 */
function mountMentionedVideosCarousel(
	videoMentions: MentionedVideoItem[],
	options: MentionedVideoMountOptions,
): HTMLDivElement {
	const mountPoint = document.createElement("div");
	mountPoint.setAttribute("data-mentioned-videos-insert", "true");
	const dispose = render(
		() => (
			<MentionedVideos
				videoMentions={videoMentions}
				allVideoMentions={options.allVideoMentions}
				appEmbedded={options.appEmbedded}
				offersClipboard={options.offersClipboard}
				onPlayVideoMentions={options.onPlayVideoMentions}
			/>
		),
		mountPoint,
	);
	options.disposers.push(dispose);
	return mountPoint;
}

/**
 * Renders a `MentionedVideosList` into a wrapper element tagged for later
 * replacement, and registers the Solid render dispose with the caller.
 */
function mountMentionedVideosList(
	videoMentions: MentionedVideoItem[],
	options: {
		appEmbedded: boolean;
		offersClipboard?: boolean;
		onPlayVideoMentions?: PlayVideoMentionsHandler;
		disposers: Array<() => void>;
	},
): HTMLDivElement {
	const mountPoint = document.createElement("div");
	mountPoint.setAttribute("data-mentioned-videos-insert", "true");
	const dispose = render(
		() => (
			<MentionedVideosList
				videoMentions={videoMentions}
				appEmbedded={options.appEmbedded}
				offersClipboard={options.offersClipboard}
				onPlayVideoMentions={options.onPlayVideoMentions}
			/>
		),
		mountPoint,
	);
	options.disposers.push(dispose);
	return mountPoint;
}

/**
 * Where to append the end-of-answer sources list. Inside the last markdown
 * segment when the answer ends with one, so its final paragraph or list is no
 * longer `:last-child` and keeps its bottom margin above the list.
 */
function endOfContentParent(container: HTMLElement): HTMLElement {
	const last = container.lastElementChild;
	return last instanceof HTMLElement &&
		last.classList.contains("speaking-markdown-segment")
		? last
		: container;
}

function applyBlockVideoPlacements(
	block: HTMLElement,
	blockPlacements: BlockVideoPlacements,
	mountOptions: MentionedVideoMountOptions,
	contentContainer?: HTMLElement,
) {
	const flushInsideContainer =
		contentContainer !== undefined && block === contentContainer;
	const insideBlock = isListItemBlock(block) || flushInsideContainer;

	if (blockPlacements.portraitThumbnail) {
		const thumbnail = mountMentionedVideoThumbnail(
			blockPlacements.portraitThumbnail,
			{
				...mountOptions,
				floatSide: "right",
			},
		);
		block.insertAdjacentElement(
			isListItemBlock(block) || flushInsideContainer
				? "afterbegin"
				: "beforebegin",
			thumbnail,
		);
	}

	if (blockPlacements.standaloneThumbnail) {
		const thumbnailBlock = mountMentionedVideoBlock(
			blockPlacements.standaloneThumbnail,
			mountOptions,
		);
		if (insideBlock) {
			block.appendChild(thumbnailBlock);
		} else {
			block.insertAdjacentElement("afterend", thumbnailBlock);
		}
	}

	if (blockPlacements.carouselMentions.length > 0) {
		const carousel = mountMentionedVideosCarousel(
			blockPlacements.carouselMentions,
			mountOptions,
		);
		if (insideBlock) {
			block.appendChild(carousel);
		} else {
			block.insertAdjacentElement("afterend", carousel);
		}
	}

	// When the mentioned-video UI was inserted as a sibling (a floated thumbnail
	// before the block, or a carousel after it) and the block held nothing but
	// the citation marker(s), the paragraph is redundant, so collapse it and let
	// the adjacent thumbnail/carousel stand in its place. We mark rather than
	// remove so the marker span survives for later re-render/enrichment passes;
	// the attribute is cleared at the start of every insert run. Skipped for
	// `insideBlock` placements (list items / container flush), where hiding the
	// block would also hide the inserted UI it contains.
	if (!insideBlock && isMentionOnlyBlock(block)) {
		block.setAttribute("data-mention-only-collapsed", "true");
	}
}

/**
 * Inserts mentioned-video UI after rich-text hydration.
 *
 * - `atEnd`: one combined list appended to the container.
 * - `afterBlock`: per major block, a single portrait mention outside tables
 *   becomes a floated thumbnail before the block; a single landscape mention
 *   becomes a centered standalone thumbnail after the block; multi-mention or
 *   in-table blocks use a carousel after the block. A single mention inside an
 *   `<li>` uses the same rules but mounts inside the list item. Inserts are
 *   deferred until the next major block begins, the next list item begins, or
 *   the answer completes.
 *
 * Clears any previous `[data-mentioned-videos-insert]` and
 * `[data-mentioned-video-thumbnail-insert]` nodes before mounting.
 * When `afterBlock` is active, also appends a combined sources list at the
 * container end for non-`appEmbedded` layouts once the answer is complete.
 * Skips the end sources list entirely when `appEmbedded` is true.
 */
function insertMentionedVideoBlocksNow(
	container: HTMLElement,
	options: {
		resolveVideoMention: (id: string) => MentionedVideoItem | undefined;
		appEmbedded: boolean;
		offersClipboard?: boolean;
		answerComplete?: boolean;
		onPlayVideoMentions?: PlayVideoMentionsHandler;
		disposers: Array<() => void>;
		placement?: MentionedVideosPlacement;
	},
) {
	const placement = options.placement ?? MENTIONED_VIDEOS_PLACEMENT;

	container
		.querySelectorAll(
			"[data-mentioned-videos-insert],[data-mentioned-video-thumbnail-insert]",
		)
		.forEach((el) => {
			el.remove();
		});

	// Restore any paragraphs collapsed on a previous run before recomputing, so
	// this pass reflects the current markup (e.g. text streamed in after the
	// marker, which would make the paragraph no longer mention-only).
	container.querySelectorAll("[data-mention-only-collapsed]").forEach((el) => {
		el.removeAttribute("data-mention-only-collapsed");
	});

	const allVideoMentions = collectVideoMentionsInOrder(
		container,
		options.resolveVideoMention,
	);

	if (placement === "atEnd") {
		if (options.appEmbedded || allVideoMentions.length === 0) {
			return;
		}

		endOfContentParent(container).appendChild(
			mountMentionedVideosList(allVideoMentions, options),
		);
		return;
	}

	const majorBlocks = collectMajorBlocksInOrder(container);
	const placementsByBlock = groupVideoMentionsByBlock(
		container,
		options.resolveVideoMention,
	);
	const mountOptions: MentionedVideoMountOptions = {
		allVideoMentions,
		appEmbedded: options.appEmbedded,
		offersClipboard: options.offersClipboard,
		onPlayVideoMentions: options.onPlayVideoMentions,
		disposers: options.disposers,
	};

	for (let blockIndex = 0; blockIndex < majorBlocks.length; blockIndex++) {
		const block = majorBlocks[blockIndex];
		const blockPlacements = placementsByBlock.get(block);
		if (!blockPlacements) {
			continue;
		}

		const shouldFlush = shouldFlushMajorBlockPlacements(
			block,
			blockIndex,
			majorBlocks,
			container,
			options.answerComplete,
		);
		if (!shouldFlush) {
			continue;
		}

		applyBlockVideoPlacements(block, blockPlacements, mountOptions, container);
	}

	for (const [block, blockPlacements] of placementsByBlock.entries()) {
		if (!isListItemBlock(block)) {
			continue;
		}

		if (!shouldFlushListItemBlock(block, options.answerComplete)) {
			continue;
		}

		applyBlockVideoPlacements(block, blockPlacements, mountOptions);
	}

	for (const [block, blockPlacements] of placementsByBlock.entries()) {
		if (
			majorBlocks.includes(block) ||
			isListItemBlock(block) ||
			options.answerComplete !== true
		) {
			continue;
		}

		applyBlockVideoPlacements(block, blockPlacements, mountOptions, container);
	}

	if (
		!options.appEmbedded &&
		allVideoMentions.length > 0 &&
		options.answerComplete === true
	) {
		endOfContentParent(container).appendChild(
			mountMentionedVideosList(allVideoMentions, options),
		);
	}
}

export function insertMentionedVideoBlocksSync(
	container: HTMLElement,
	options: {
		resolveVideoMention: (id: string) => MentionedVideoItem | undefined;
		appEmbedded: boolean;
		offersClipboard?: boolean;
		answerComplete?: boolean;
		onPlayVideoMentions?: PlayVideoMentionsHandler;
		disposers: Array<() => void>;
		placement?: MentionedVideosPlacement;
	},
) {
	if (!container.isConnected) {
		return;
	}
	insertMentionedVideoBlocksNow(container, options);
}
